import { useEffect, useRef } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { Icon } from '@/design-system'
import { useQueue } from '@/features/offline/queue'
import { flags } from '@/app/config'
import './Dock.css'

export function Dock({ pendingRequests = 0 }: { pendingRequests?: number }) {
  const navigate = useNavigate()
  const pendingDrafts = useQueue((s) => s.pending)
  const barRef = useRef<HTMLElement | null>(null)
  const { pathname } = useLocation()

  // iOS/WebKit can freeze a fixed element at document coordinates after a
  // full-screen overlay above it unmounts. Nudging the compositing layer on
  // every route change and every return to the foreground re-anchors it —
  // healing even a bar that is already stuck mid-screen.
  useEffect(() => {
    const nudge = () => {
      const el = barRef.current
      if (!el) return
      el.style.transform = 'translateZ(0) translateY(0.01px)'
      requestAnimationFrame(() => {
        el.style.transform = ''
      })
    }
    nudge()
    const onShow = () => {
      if (document.visibilityState === 'visible') nudge()
    }
    document.addEventListener('visibilitychange', onShow)
    window.addEventListener('pageshow', nudge)
    return () => {
      document.removeEventListener('visibilitychange', onShow)
      window.removeEventListener('pageshow', nudge)
    }
  }, [pathname])

  return (
    <nav className="dock" aria-label="Navegación principal" ref={barRef}>
      <div className="dock__bar">
        <NavLink to="/" end className="dock__item" aria-label="Inicio">
          <Icon name="home" size={26} />
        </NavLink>

        <NavLink to="/mapa" className="dock__item" aria-label="Mi mapa">
          <Icon name="map" size={26} />
        </NavLink>

        <button
          type="button"
          className="lens-btn"
          onClick={() => navigate('/capturar')}
          aria-label="Encontré un gato"
        >
          <Icon name="cat-camera" size={32} />
          {pendingDrafts > 0 && (
            <span className="lens-btn__pending" aria-label="Pendiente de sincronizar">
              <Icon name="cloud" size={12} strokeWidth={2.4} />
            </span>
          )}
        </button>

        {flags.discover ? (
          <NavLink to="/descubrir" className="dock__item" aria-label="Descubrir">
            <Icon name="compass" size={26} />
          </NavLink>
        ) : (
          <span className="dock__item" aria-hidden="true" />
        )}

        <NavLink to="/perfil" className="dock__item" aria-label="Mi perfil">
          <Icon name="user" size={26} />
          {pendingRequests > 0 && (
            <span className="dock__badge">{pendingRequests > 9 ? '9+' : pendingRequests}</span>
          )}
        </NavLink>
      </div>
    </nav>
  )
}
